import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { differenceInSeconds, parseISO } from "date-fns"
import { fromZonedTime } from "date-fns-tz"
import { BadgeCheckIcon, Loader2Icon, MailIcon } from "lucide-react"
import { useEffect, useState } from "react"
import {
  DocumentSignee,
  EmailVerificationStatus,
  getEmailVerificationStatus,
  getSignee,
  sendVerificationCode,
  validateEmailCode,
} from "@/api/api"
import { NextStep } from "@/components/next-step"
import { PageLoader } from "@/components/page-loader"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/ui/input-otp"
import { useCountdown } from "@/lib/use-countdown"
import { useCurrentToken } from "@/lib/use-current-token"
import { formatSeconds } from "@/lib/utils"

type EmailVerificatorProps = {
  signee: DocumentSignee
  status: EmailVerificationStatus
  token_id: string
}

const EmailVerificator = ({
  signee,
  status,
  token_id,
}: EmailVerificatorProps) => {
  const queryClient = useQueryClient()
  const [code, setCode] = useState<string>("")

  const sendCodeMutation = useMutation({
    mutationKey: ["sendVerificationCode", { token_id }],
    mutationFn: sendVerificationCode,
    onSuccess: () =>
      queryClient.refetchQueries({
        queryKey: ["getEmailVerificationStatus", { token_id }],
      }),
  })

  const validateCodeMutation = useMutation({
    mutationKey: ["validateEmailCode", { token_id }],
    mutationFn: validateEmailCode,
    onSuccess: () =>
      queryClient.refetchQueries({
        queryKey: ["getEmailVerificationStatus", { token_id }],
      }),
  })

  const [count, { startCountdown, setCount }] = useCountdown({
    countStart: 0,
    countStop: 0,
    intervalMs: 1000,
  })

  useEffect(() => {
    if (status.status == "email_code_sent") {
      const sentAt = fromZonedTime(parseISO(status.sent_at), "UTC")
      const now = new Date()
      const diff = 60 - differenceInSeconds(now, sentAt)

      if (diff <= 0) {
        return
      }

      setCount(diff)
      startCountdown()
    }
  }, [status, startCountdown, setCount])

  if (status.status == "unverified") {
    return (
      <>
        <p className="pb-6 text-center">
          Haz click en el botón para enviar un código temporal de 6 digitos a la
          dirección de correo electrónico <strong>{signee.email}</strong>. El
          código tiene una duración de 60 segundos
        </p>

        <Button
          type="button"
          className="w-full md:w-auto"
          disabled={sendCodeMutation.isPending}
          onClick={() => sendCodeMutation.mutate({ token_id })}
        >
          {sendCodeMutation.isPending ? (
            <>
              <Loader2Icon className="mr-2 animate-spin" />
              <span>Enviando...</span>
            </>
          ) : (
            <span>Enviar código</span>
          )}
        </Button>
      </>
    )
  }

  if (status.status == "email_code_sent") {
    return (
      <div className="flex flex-col items-center gap-6">
        <p className="text-center">
          Se ha enviado el código de confirmación a tu correo electrónico.
          Revisa tu casilla de correo no deseado en caso de que no lo
          encuentres.
        </p>

        <div>
          <InputOTP
            maxLength={6}
            autoFocus
            pushPasswordManagerStrategy="none"
            value={code}
            disabled={validateCodeMutation.isPending}
            onChange={(value) => setCode(value)}
            onComplete={() =>
              validateCodeMutation.mutate({ token_id, code: parseInt(code) })
            }
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
            </InputOTPGroup>
            <InputOTPSeparator />
            <InputOTPGroup>
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
        </div>

        {validateCodeMutation.isError && (
          <p className="text-destructive">
            Código ingresado no es válido. Reintente nuevamente.
          </p>
        )}

        <div className="flex gap-4">
          <Button
            type="button"
            variant={count === 0 ? "default" : "secondary"}
            disabled={
              count > 0 ||
              validateCodeMutation.isPending ||
              sendCodeMutation.isPending
            }
            onClick={() => {
              sendCodeMutation.reset()
              sendCodeMutation.mutate({ token_id })
            }}
          >
            {sendCodeMutation.isPending ? (
              <>
                <Loader2Icon className="mr-2 animate-spin" />
                <span>Reenviando código...</span>
              </>
            ) : validateCodeMutation.isPending ? (
              <>
                <Loader2Icon className="mr-2 animate-spin" />
                <span>Validando código...</span>
              </>
            ) : count === 0 ? (
              <span>Reenviar código</span>
            ) : (
              <span>Reenviar código en {formatSeconds(count)}</span>
            )}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <>
      <BadgeCheckIcon size={64} className="mb-6 text-primary" />

      <p className="pb-6 text-center">
        ¡Tu correo ha sido validado exitosamente!
      </p>

      <NextStep />
    </>
  )
}

const EmailVerifyPage = () => {
  const { token_id, document_id, signee_id, isReady } = useCurrentToken()

  const signeeQuery = useQuery({
    queryKey: ["getSignee", { document_id, signee_id, token_id }],
    queryFn: () =>
      getSignee({ document_id: document_id!, signee_id: signee_id!, token_id }),
    enabled: isReady && !!document_id && !!signee_id,
  })

  const statusQuery = useQuery({
    queryKey: ["getEmailVerificationStatus", { token_id }],
    queryFn: () => getEmailVerificationStatus({ token_id }),
    enabled: isReady,
  })

  if (!isReady || signeeQuery.isLoading || statusQuery.isLoading) {
    return <PageLoader />
  }

  if (signeeQuery.isError || statusQuery.isError) {
    throw signeeQuery.error || statusQuery.error
  }

  if (
    !signeeQuery.isSuccess ||
    !signeeQuery.data ||
    !statusQuery.isSuccess ||
    !statusQuery.data
  ) {
    throw new Error("Error inesperado")
  }

  return (
    <div className="mx-auto md:w-10/12 lg:w-8/12">
      <Card className="shadow-lg">
        <CardHeader>
          <div className="flex flex-col items-center justify-center md:flex-row">
            <MailIcon className="md:mr-3" />
            <h1 className="text-center text-xl md:text-2xl">
              Verificación de correo electrónico
            </h1>
          </div>
        </CardHeader>

        <CardContent className="flex flex-col items-center justify-center">
          <EmailVerificator
            signee={signeeQuery.data}
            token_id={token_id}
            status={statusQuery.data}
          />
        </CardContent>
      </Card>
    </div>
  )
}

export { EmailVerifyPage }
